/**
 * The app's long-lived services, created once at start-up and shared through
 * React context. Tests build their own with fakes.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { ApiClient } from '../lib/api/client';
import { createAuthProvider, type AuthProvider } from '../lib/auth';
import { createOfflineStorage, type OfflineStorage } from '../lib/offline';
import { createQueryClient, type QueryClient } from '../lib/query';
import { markServerContact } from '../lib/session';
import { isEventOp } from '@sss/sync';
import { HttpTransport } from '../lib/sync/transport';
import { SyncService } from '../lib/sync/sync-service';
import { DraftService } from '../features/capture/draft-service';
import { onReportSent } from '../features/events/event-outbox';

export interface Services {
  api: ApiClient;
  auth: AuthProvider;
  storage: OfflineStorage;
  sync: SyncService;
  drafts: DraftService;
  queryClient: QueryClient;
  /** Notified by the transport/API about app-wide conditions. */
  events: AppEvents;
}

export type AppEvent = { type: 'upgrade-required' } | { type: 'account-error'; code: string; wipe: boolean };

export class AppEvents {
  private readonly listeners = new Set<(e: AppEvent) => void>();
  emit(e: AppEvent) {
    this.listeners.forEach((l) => l(e));
  }
  subscribe(l: (e: AppEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
}

export function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_URL ?? 'http://localhost:8080').replace(/\/$/, '');
}

export function createServices(overrides: Partial<Pick<Services, 'auth' | 'storage'>> & { fetchImpl?: typeof fetch; baseUrl?: string } = {}): Services {
  const events = new AppEvents();
  const auth = overrides.auth ?? createAuthProvider();
  const storage = overrides.storage ?? createOfflineStorage();
  const api = new ApiClient({
    baseUrl: overrides.baseUrl ?? apiBaseUrl(),
    tokens: auth,
    fetchImpl: overrides.fetchImpl,
    onServerContact: () => markServerContact(),
    onAuthFailure: (e) => {
      if (e.status === 403) events.emit({ type: 'account-error', code: e.code, wipe: !!(e.details as { wipe?: boolean } | undefined)?.wipe });
    },
  });
  // The draft service needs the sync service, and the transport reports back to
  // the draft service — wire through a late-bound reference.
  let drafts: DraftService | null = null;
  const transport = new HttpTransport(api, storage, {
    // Event reports (ADR-0029) have no draft: a refused report stays in the outbox,
    // visible on the Events screen with Retry/Delete, rather than being dropped.
    onResult: (item, data) => (isEventOp(item.op) ? onReportSent(storage, item) : drafts?.onResult(item, data)),
    onReject: (item, error, details) => (isEventOp(item.op) ? undefined : drafts?.onReject(item, error, details)),
    onAttachmentRefused: (item, id, code) => drafts?.onAttachmentRefused(item, id, code),
    // Account errors on sync requests reach `events` once, through the ApiClient's onAuthFailure above.
    onUpgradeRequired: () => events.emit({ type: 'upgrade-required' }),
  });
  const sync = new SyncService(storage, transport);
  drafts = new DraftService(storage, sync);
  return { api, auth, storage, sync, drafts, queryClient: createQueryClient(), events };
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const s = useContext(ServicesContext);
  if (!s) throw new Error('ServicesProvider missing');
  return s;
}

export const useApi = () => useServices().api;
